import { useCallback } from 'react';
import { usePackageCart } from './usePackageCart';
import { toast } from 'sonner';

/**
 * Hook for transferring guest cart items to authenticated user cart
 * Uses localStorage as source of truth for both products and packages
 * Merges quantities if items already exist in authenticated cart
 */
export const useCartTransfer = () => {
  const { refetch } = usePackageCart();

  const transferGuestCartToAuthenticatedUser = useCallback(async (userId: string) => {
    try {
      // Get current authenticated user's cart data
      const authenticatedCart = localStorage.getItem(`package-cart-${userId}`);
      const authCartItems = authenticatedCart ? JSON.parse(authenticatedCart) : [];

      // Get guest cart data (empty key = guest)
      const guestCart = localStorage.getItem('package-cart-');
      const guestCartItems = guestCart ? JSON.parse(guestCart) : [];

      if (!guestCartItems || guestCartItems.length === 0) {
        return;
      }

      console.log('Transferring guest cart to authenticated user:', {
        userId,
        itemCount: guestCartItems.length
      });

      // Merge guest items into authenticated cart
      const mergedItems = [...authCartItems];

      for (const guestItem of guestCartItems) {
        const existingIndex = mergedItems.findIndex(
          item =>
            item.item_type === guestItem.item_type &&
            ((guestItem.item_type === 'product' && item.product_id === guestItem.product_id) ||
              (guestItem.item_type === 'package' && item.package_id === guestItem.package_id))
        );

        if (existingIndex >= 0) {
          // Merge quantities
          mergedItems[existingIndex].quantity += guestItem.quantity;
          // Preserve authenticated user's customizations if they exist
          if (!mergedItems[existingIndex].customizations && guestItem.customizations) {
            mergedItems[existingIndex].customizations = guestItem.customizations;
          }
        } else {
          // Add new item from guest cart
          mergedItems.push(guestItem);
        }
      }

      // Save merged cart to authenticated user's cart
      localStorage.setItem(`package-cart-${userId}`, JSON.stringify(mergedItems));

      // Clear guest cart
      localStorage.removeItem('package-cart-');

      // Refresh cart queries
      if (refetch) {
        await refetch();
      }

      // Show success message
      toast.success(`Welcome back! ${guestCartItems.length} item(s) transferred to your cart.`);

      console.log('Guest cart transfer completed successfully');
    } catch (error) {
      console.error('Error transferring guest cart:', error);
      // Don't show error to user - just log it
      // The user can still see their authenticated cart if transfer fails
    }
  }, [refetch]);

  return { transferGuestCartToAuthenticatedUser };
};
